import React, { useCallback, useState, useEffect } from 'react';
import styles from './SalonProfileTop.module.scss';
import { ReactComponent as IconUploadBg } from 'images/icons/upload_bg.svg';
import { ReactComponent as IconUploadPhoto } from 'images/icons/upload_photo.svg';
import { ReactComponent as Star } from 'images/new-icons/yellow-star.svg';
import { ReactComponent as HalfStar } from 'images/new-icons/half-yellow-star.svg';
import { ReactComponent as GreyStar } from 'images/new-icons/grey-star.svg';
import Avatar from 'components/shared/Avatar';
import { cloudApi, supabase } from 'sdk/api';
import useUpdateSalon from 'sdk/hooks/salon/useUpdateSalon';
import clsx from 'clsx';
import useUser from 'sdk/hooks/user/useUser';
import useSalonsFollowersFollowingCount from 'sdk/hooks/follow/useSalonsFollowersFollowingCount';
import Modal from 'components/shared/modals/Modal';
import Cropper from 'react-easy-crop';
import Loader from 'components/shared/Loader/Loader';
import Button from 'components/shared/Button';
import getCroppedImg from 'utils/cropImage';
import { ReactComponent as IconCalendar } from 'images/calendar.svg';
import Tabs from 'components/shared/Tabs';
import { CircleFlag } from 'react-circle-flags';
import { countries } from 'utils/countries';
import { useNavigate } from 'react-router-dom';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { useTranslation } from 'react-i18next';
const SalonProfileTop = ({ data, refetch, tab, setTab, tabs, onBookClick }: any) => {
  const userData = useUser();
  const userId = userData?.data?.user?.id || '';
  const {t} = useTranslation() as any
  const navigate = useNavigate();
  const { data: isAuthData } = useIsAuth();

  const [isLoadingBg, setIsLoadingBg] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [image, setImage] = useState();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);

  const updateSalon = useUpdateSalon({ id: data?.id });
  const { data: count, refetch: refetchCount } = useSalonsFollowersFollowingCount(userId);

  const profileBackgroundVideo = data?.bg_cover;

  // Fetch and calculate average rating from reviews
  useEffect(() => {
    const fetchAverageRating = async () => {
      if (!data?.id) return;
      
      const { data: reviews, error } = await supabase
        .from('reviews')
        .select('rank')
        .eq('salon', data.id);
      
      if (error) {
        console.error('Error fetching reviews:', error);
        return;
      }
      
      if (reviews && reviews.length > 0) {
        const total = reviews.reduce((sum, review) => sum + (review.rank || 0), 0);
        const average = (total / reviews.length).toFixed(1);
        setAverageRating(parseFloat(average));
        setReviewsCount(reviews.length);
      } else {
        setAverageRating(0);
        setReviewsCount(0);
      }
    };
    
    fetchAverageRating();
  }, [data?.id]);

  const onCropComplete = useCallback((croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onBgUpload = async () => {
    setIsLoadingBg(true);
    const bgForm = document.getElementById('bgForm') as HTMLFormElement;
    const formData = new FormData(bgForm);
    const response = await cloudApi.uploadMedia(formData);
    setIsLoadingBg(false);
    updateSalon.mutate(
      { bg_cover: response },
      {
        onSuccess() {
          refetch();
        },
      },
    );
  };

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage: any = await getCroppedImg(image, croppedAreaPixels);

      const response = await cloudApi.uploadMedia(croppedImage);

      await updateSalon.mutate(
        { avatar: response },
        {
          onSuccess() {
            refetch();
          },
        },
      );

      setImage(undefined);
      setImageModal(false);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels]);

  const getMedia = (link: string) => {
    if (!link) {
      return '';
    }

    if (link.slice(-3) === 'mp4' || link.slice(-3) === 'mov' || link.slice(-3) === '3gp') {
      return <video src={link} muted autoPlay loop />;
    }

    return <img src={link} alt={data?.username} />;
  };

  const onAvatarUpload = async () => {
    const avatarForm = document.getElementById('avatarForm') as HTMLFormElement;
    const formData = new FormData(avatarForm);
    setImageModal(true);
    const response = await cloudApi.uploadMedia(formData);

    setImage(response);
  };

  const getCountryCode = (countryName: string) => {
    const country = countries.find(item => item.name === countryName);
    return country ? country.isoCode.toLowerCase() : '';
  };

  const renderStars = () => {
    // Return just one star icon
    return <Star />;
  };
  return (
    <>
      <div className={styles.container}>
        <div className={clsx(styles.profile, isLoadingBg ? 'blockLoading' : '')}>
          {profileBackgroundVideo ? (
            <div className={styles.profileImg}>{getMedia(data?.bg_cover)}</div>
          ) : (
            <form id="bgForm" className={styles.bgUploadLabel}>
              <label htmlFor="bgUpload">
                <input
                  type="file"
                  name="file"
                  id="bgUpload"
                  className={styles.bgUploadInput}
                  onChange={onBgUpload}
                  accept={'image/*, video/*'}
                />
                <IconUploadBg />
                {t('Upload background')}
              </label>
            </form>
          )}
          <div className={styles.avatarRow}>
            {/* {
            expertProfile?.bg_video && (
              <IconPlay className={styles.playIcon} onClick={() => setIsOpenFullBgVideo(true)} />
            )
          } */}

            <Modal
              onClose={() => setImageModal(false)}
              isOpen={imageModal}
              onClick={(e: any) => e.stopPropagation()}
              className={styles.cropper}
            >
              <div className={styles.cropContainer}>
                {image ? (
                  <Cropper
                    image={image}
                    crop={crop}
                    onCropChange={setCrop}
                    zoom={zoom}
                    aspect={4 / 4}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    restrictPosition
                  />
                ) : (
                  <Loader className={styles.cropLoader} />
                )}
              </div>

              <Button wide onClick={showCroppedImage}>
                {t('Apply')}
              </Button>
            </Modal>
            {!data?.avatar ? (
              <form id="avatarForm" className={styles.avatar}>
                <label htmlFor="avatarUpload">
                  <input
                    type="file"
                    name="file"
                    id="avatarUpload"
                    className={styles.bgUploadInput}
                    onChange={onAvatarUpload}
                    accept="image/*"
                  />
                  <IconUploadPhoto />
                </label>
              </form>
            ) : (
              <Avatar
                className={styles.avatar}
                image={data?.avatar}
                isExpert={false}
              />
            )}
          </div>
          {/* <div className={styles.followers} onClick={() => console.log('onFollowersClick')}>
            {!count?.followers ? (
              <div className={styles.followersValue}>{count?.followers || '0'}</div>
            ) : (
              <div className={styles.followersValue} />
            )}
            {!count?.followers ? (
              <div className={styles.followersTitle}>{t('Followers')}</div>
            ) : (
              <div className={styles.followersValue} />
            )}
          </div> */}
          <div className={styles.rating}>
            <div className={styles.ratingStars}>
              {renderStars()}
            </div>
            <div className={styles.ratingInfo}>
              <span className={styles.ratingValue}>{averageRating.toFixed(1)}</span>
            </div>
          </div>
          <div className={styles.location}>
            {data?.country ? (
              <div className={styles.countryRow}>
                <CircleFlag
                  countryCode={data?.country?.iso?.toLowerCase() || ''}
                  height="20"
                  className={styles.flag}
                />
                <span className={styles.countryName}>{data?.country?.country_name}</span>
              </div>
            ) : (
              ''
            )}
          </div>
          <div className={styles.name}>{data?.name}</div>
          <div className={styles.profileActions}>
            <Button
              darkGreen
              className={styles.bookButton}
              onClick={() => {
                if (isAuthData?.isAuth) {
                  onBookClick();
                } else {
                  navigate('/sign-up');
                }
              }}
            >
              <IconCalendar />
               {t('Book')}
            </Button>
          </div>
          <Tabs className={styles.tabs} value={tab} items={tabs} onSelect={setTab} inline gray />
        </div>
      </div>
    </>
  );
};

export default SalonProfileTop;
